import { Fragment, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { Editions, ProductKeys } from '@atlassiansox/asc-stargate';

import { useCrossFlowFrontendTouchpoint } from '@/components/cross-flow/touchpoint/use-touchpoint/index';
import { JiraSmartBanner } from '@/components/smart-banner/src/jira-smart-banner';
import { useDarkTheme } from '@/libraries/design/utils/dark-theme-provider';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { LogoV3 } from '@/libraries/wpl/foundations/src/logo-v3';
import { DropdownContent } from '@/patterns/global-nav/src/dropdown-content';
import { GlobalNavLinkContentProps } from '@/patterns/global-nav/src/nav-links-container';
import { GlobalNavProvider } from '@/patterns/global-nav/src/nav-links-container';
import { ProductPageSubNav } from '@/patterns/global-nav/src/nav-product-page';

import { FeaturesDropdown } from './features-dropdown/features-dropdown';
import { TemplateCategoriesDropdown } from './template-categories-dropdown/template-categories-dropdown';

const JiraLogo = () => {
	const { isDarkTheme } = useDarkTheme();
	return <LogoV3 brand="jira" type="logo" appearance="brand" lightMode={isDarkTheme} />;
};

export type JiraSoftwareNavProps = {
	productNavCtaCTAClickFunction?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	isUseOpenCrossFlow?: boolean;
};

const JiraSoftwareNav = ({
	productNavCtaCTAClickFunction,
	isUseOpenCrossFlow = false,
}: JiraSoftwareNavProps) => {
	const intl = useIntl();
	const ctaText = intl.formatMessage({
		id: 'JiraFeatureSoftwarePageTemplate.navbar.getItFree',
		defaultMessage: 'Get it free',
	});

	const signupLink = useLocalizedHref('/try/cloud/signup?bundle=jira-software&edition=free');

	const { openCrossFlow } = useCrossFlowFrontendTouchpoint({
		productKey: ProductKeys.JIRA_SOFTWARE,
		edition: Editions.FREE,
		redirectUrl: signupLink,
	});

	const productNavOnClick = useMemo(
		() =>
			productNavCtaCTAClickFunction && isUseOpenCrossFlow
				? productNavCtaCTAClickFunction
				: openCrossFlow,
		[openCrossFlow, productNavCtaCTAClickFunction, isUseOpenCrossFlow],
	);

	const solutionsDropdownStyles = useMemo(() => {
		return {
			width: 'fit-content !important',
			margin: 0,
			transform: 'translateX(-40%)',
		};
	}, []);

	// Teams Hrefs
	const marketingHref = useLocalizedHref('/jira/solutions/marketing');
	const engineeringHref = useLocalizedHref('/jira/solutions/engineering');
	const designHref = useLocalizedHref('/jira/solutions/design');
	const operationsHref = useLocalizedHref('/jira/solutions/operations');
	const itHref = useLocalizedHref('/jira/solutions/it');

	// Use Cases Hrefs
	const gettingStartedHref = useLocalizedHref('https://community.atlassian.com/learning/hub/jira');
	const planningHref = useLocalizedHref('/jira/solutions/planning');
	const campaignManagementHref = useLocalizedHref('/jira/solutions/campaign-management');
	const agileProjectManagementHref = useLocalizedHref('/jira/solutions/agile-project-management');
	const programManagementHref = useLocalizedHref('/jira/solutions/program-management');

	//Company Size Hrefs
	const enterpriseHref = useLocalizedHref('/software/jira/enterprise');

	const solutionsDropdown = useMemo(
		() => (
			<DropdownContent
				contentSections={[
					{
						header: intl.formatMessage({
							id: 'software-jira.navbar.solutions.teams.header',
							defaultMessage: 'Teams',
						}),
						links: [
							{
								text: intl.formatMessage({
									id: 'software-jira.navbar.solutions.teams.marketing',
									defaultMessage: 'Marketing',
								}),
								href: marketingHref,
							},
							{
								text: intl.formatMessage({
									id: 'software-jira.navbar.solutions.teams.engineering',
									defaultMessage: 'Engineering',
								}),
								href: engineeringHref,
							},
							{
								text: intl.formatMessage({
									id: 'software-jira.navbar.solutions.teams.design',
									defaultMessage: 'Design',
								}),
								href: designHref,
							},
							{
								text: intl.formatMessage({
									id: 'software-jira.navbar.solutions.teams.operations',
									defaultMessage: 'Operations',
								}),
								href: operationsHref,
							},
							{
								text: intl.formatMessage({
									id: 'software-jira.navbar.solutions.teams.it',
									defaultMessage: 'IT',
								}),
								href: itHref,
							},
						],
					},
					{
						header: intl.formatMessage({
							id: 'software-jira.navbar.solutions.useCases.header',
							defaultMessage: 'Use Cases',
						}),
						links: [
							{
								text: intl.formatMessage({
									id: 'software-jira.navbar.solutions.useCases.getting-started',
									defaultMessage: 'Getting started',
								}),
								href: gettingStartedHref,
							},
							{
								text: intl.formatMessage({
									id: 'software-jira.navbar.solutions.useCases.planning',
									defaultMessage: 'Planning',
								}),
								href: planningHref,
							},
							{
								text: intl.formatMessage({
									id: 'software-jira.navbar.solutions.useCases.campaign-management',
									defaultMessage: 'Campaign Management',
								}),
								href: campaignManagementHref,
							},
							{
								text: intl.formatMessage({
									id: 'software-jira.navbar.solutions.useCases.agile-project-management',
									defaultMessage: 'Agile Project Management',
								}),
								href: agileProjectManagementHref,
							},
							{
								text: intl.formatMessage({
									id: 'software-jira.navbar.solutions.useCases.program-management',
									defaultMessage: 'Program Management',
								}),
								href: programManagementHref,
							},
						],
					},
					{
						header: intl.formatMessage({
							id: 'software-jira.navbar.solutions.companySize.header',
							defaultMessage: 'Company Size',
						}),
						links: [
							{
								text: intl.formatMessage({
									id: 'software-jira.navbar.solutions.companySize.enterprise',
									defaultMessage: 'Enterprise',
								}),
								href: enterpriseHref,
							},
						],
					},
				]}
			/>
		),
		[
			intl,
			gettingStartedHref,
			marketingHref,
			engineeringHref,
			designHref,
			operationsHref,
			itHref,
			planningHref,
			campaignManagementHref,
			agileProjectManagementHref,
			programManagementHref,
			enterpriseHref,
		],
	);

	const guidesHref = useLocalizedHref('/software/jira/guides/getting-started/introduction');
	const pricingHref = useLocalizedHref('/software/jira/pricing');
	const productLogoHref = useLocalizedHref('/software/jira');

	const templatesLinkContent = useMemo<GlobalNavLinkContentProps>(() => {
		return {
			globalLinksKey: 'templates',
			linkText: intl.formatMessage({
				id: 'software-jira.navbar.templates',
				defaultMessage: 'Templates',
			}),
			productKey: 'jira',
			hasSubnav: true,
			content: <TemplateCategoriesDropdown />,
			dropdownType: 'templates',
			containerPosition: 'relative',
		};
	}, [intl]);

	const featuresLinkContent = useMemo<GlobalNavLinkContentProps>(
		() => ({
			globalLinksKey: 'features',
			linkText: intl.formatMessage({
				id: 'software-jira.navbar.features',
				defaultMessage: 'Features',
			}),
			productKey: 'jira',
			hasSubnav: true,
			content: <FeaturesDropdown />,
			dropdownType: 'features',
			containerPosition: 'relative',
		}),
		[intl],
	);

	const links: GlobalNavLinkContentProps[] = useMemo(
		() => [
			featuresLinkContent,
			{
				globalLinksKey: 'solutions',
				linkText: intl.formatMessage({
					id: 'software-jira.navbar.solutions',
					defaultMessage: 'Solutions',
				}),
				hasSubnav: true,
				productKey: 'jira',
				containerPosition: 'relative',
				containerStyles: solutionsDropdownStyles,
				dropdownType: 'solutions',
				content: solutionsDropdown,
			},
			{
				globalLinksKey: 'guides',
				linkText: intl.formatMessage({
					id: 'software-jira.navbar.guides',
					defaultMessage: 'Guides',
				}),
				hasSubnav: false,
				productKey: 'jira',
				href: guidesHref,
			},
			templatesLinkContent,
			{
				globalLinksKey: 'pricing',
				linkText: intl.formatMessage({
					id: 'software-jira.navbar.pricing',
					defaultMessage: 'Pricing',
				}),
				hasSubnav: false,
				productKey: 'jira',
				href: pricingHref,
			},
		],
		[
			intl,
			featuresLinkContent,
			templatesLinkContent,
			solutionsDropdown,
			solutionsDropdownStyles,
			guidesHref,
			pricingHref,
		],
	);

	const globalNavItemKeys = useMemo(() => {
		return links.map((link) => link.globalLinksKey);
	}, [links]);

	return (
		<Fragment>
			<JiraSmartBanner />
			<GlobalNavProvider globalNavItemKeys={globalNavItemKeys}>
				<ProductPageSubNav
					productLogoUrl={productLogoHref}
					productLogo={<JiraLogo />}
					productLogoAriaLabel={intl.formatMessage({
						id: 'wacJiraSoftwareNavigation.logo-label',
						defaultMessage: 'Jira Software logo',
					})}
					mobileCtaText={intl.formatMessage({
						id: 'JiraeSoftwarePageTemplate.navbar.getitfree',
						defaultMessage: 'Get it free',
					})}
					mobileCtaUrl={signupLink}
					mobileCtaOnClick={(e: React.MouseEvent) => {
						if (productNavOnClick) {
							productNavOnClick(e as React.MouseEvent<HTMLButtonElement>);
						}
					}}
					mobileLogo={<JiraLogo />}
					navigationTrynowCtaText={ctaText}
					navigationTrynowCtaUrl={signupLink}
					navigationTrynowCtaOnClick={(e: React.MouseEvent) => {
						if (productNavOnClick) {
							productNavOnClick(e as React.MouseEvent<HTMLButtonElement>);
						}
					}}
					productPageNavLinks={links}
					mobileLinksSections={[
						{
							mobileLinkKey: 'mobile-jira',
							mobileLinksProps: links,
						},
					]}
					additionalMenuPageObjectsArray={[
						{
							menuPageKey: 'features',
							menuPageObjects: links.find((link) => link.globalLinksKey === 'features')
								?.content as JSX.Element,
						},
						{
							menuPageKey: 'solutions',
							menuPageObjects: links.find((link) => link.globalLinksKey === 'solutions')
								?.content as JSX.Element,
						},
						{
							menuPageKey: 'templates',
							menuPageObjects: links.find((link) => link.globalLinksKey === 'templates')
								?.content as JSX.Element,
						},
					]}
				/>
			</GlobalNavProvider>
		</Fragment>
	);
};

JiraSoftwareNav.displayName = 'JiraSoftwareNav';

export default JiraSoftwareNav;
